import React, { useEffect, useState } from 'react'
import { Row, Col, Card, Statistic, Progress } from 'antd'
import {
  UserOutlined,
  LogoutOutlined,
  TeamOutlined,
  WarningOutlined,
  AlertOutlined,
} from '@ant-design/icons'
import CountUp from 'react-countup'
import axiosInstance from '../../Utils/axiosInstance'
import { useNavigate } from 'react-router-dom'

import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import {
  DashboardAPI,
  GetExtensionRemainderData,
  GetFormCompletionStatus,
  GetStudentsBelowFiftyPercent,
} from '../APIComponents/AdminApi'
import './Dashboard.css'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Legend,
} from 'recharts'
import axios from 'axios'

const Dashboard = () => {
  const navigate = useNavigate()

  // ---------------- Auth ----------------
  const isLoggedIn = !!sessionStorage.getItem('userData')

  useEffect(() => {
    if (!isLoggedIn) navigate('/')
  }, [isLoggedIn, navigate])

  // Disable back
  useEffect(() => {
    window.history.pushState(null, '', window.location.href)
    window.onpopstate = function () {
      window.history.pushState(null, '', window.location.href)
    }
    return () => (window.onpopstate = null)
  }, [])

  // ---------------- State ----------------
  const [stats, setStats] = useState({})
  const [formCompletionStatus, setFormCompletionStatus] = useState([])
  const [loading, setLoading] = useState(true)

  const [lowCompletionStudents, setLowCompletionStudents] = useState([])
  const [lowLoading, setLowLoading] = useState(false)

  const [extensionData, setExtensionData] = useState({
    days_5: [],
    days_8: [],
    days_15: [],
  })

  // ---------------- API ----------------

  useEffect(() => {
    fetchStats()
    fetchExtensionData()
    fetchCompletionStatusData()
    fetchLowCompletionStudents()
  }, [])

  const fetchStats = async () => {
    try {
      const res = await axiosInstance.get(DashboardAPI)
      setStats(res.data)
    } catch (err) {
      console.error('Stats API error:', err)
    }
  }

  const fetchExtensionData = async () => {
    try {
      setLoading(true)

      const res = await axiosInstance.get(GetExtensionRemainderData)

      setExtensionData({
        days_5: res.data.days_5 || [],
        days_8: res.data.days_8 || [],
        days_15: res.data.days_15 || [],
      })
    } catch (err) {
      console.error('Extension API error:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchCompletionStatusData = async () => {
    try {
      const res = await axios.get(GetFormCompletionStatus)

      const apiData = res.data

      //  safety check
      if (!apiData || typeof apiData !== 'object') {
        setFormCompletionStatus([])
        return
      }

      const formattedData = Object.entries(apiData)
        .filter(([key]) => key !== 'totalStudents')
        .map(([key, value]) => ({
          name: key.replace(/([A-Z])/g, ' $1'), // better labels
          value: Math.min(parseFloat(value) || 0, 100),
        }))

      setFormCompletionStatus(formattedData)
    } catch (error) {
      console.error('Chart API Error:', error)
      setFormCompletionStatus([]) // fallback
    }
  }

  const fetchLowCompletionStudents = async () => {
    try {
      setLowLoading(true)

      const res = await axiosInstance.get(GetStudentsBelowFiftyPercent)

      // Filter < 50% (extra safety)
      const filtered = (res.data || []).filter(
        (item) => parseFloat(item.percentage) < 50,
      )

      setLowCompletionStudents(filtered)
    } catch (err) {
      console.error('Low completion API error:', err)
      setLowCompletionStudents([])
    } finally {
      setLowLoading(false)
    }
  }

  // ---------------- Formatter ----------------
  const formatter = (value) => (
    <CountUp end={value || 0} duration={1.5} separator="," />
  )

  // ---------------- Extension Card ----------------
  const getDaysLeft = (date) => {
    const today = new Date()
    const target = new Date(date)

    const diffTime = target - today
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    return diffDays
  }

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  }

  const ExtensionCard = ({ title, data, color }) => {
    return (
      <Card
        className="extension-card shadow"
        loading={loading}
        title={
          <div className="d-flex justify-content-between">
            <span style={{ color }}>{title}</span>
            <span className="count-badge">{data.length}</span>
          </div>
        }
      >
        <div className="extension-list">
          {data.length > 0 ? (
            data.map((item, index) => {
              const daysLeft = getDaysLeft(item.date)

              return (
                <div key={index} className="extension-item">
                  <div className="left">
                    <span className="dot" style={{ background: color }} />
                    <div>
                      <div className="name">{item.student?.full_name}</div>
                      <div className="sub-text">
                        Expiry: {formatDate(item.date)}
                      </div>
                    </div>
                  </div>

                  {/* <div
                    className={`days-badge ${daysLeft <= 5 ? 'urgent' : ''}`}
                  >
                    {daysLeft}d
                  </div> */}
                </div>
              )
            })
          ) : (
            <div className="no-data">No Students</div>
          )}
        </div>
      </Card>
    )
  }

  const LowCompletionCard = ({ data }) => {
    return (
      <Card
        title="⚠️ Students Below 50% Completion"
        className="shadow"
        loading={lowLoading}
        extra={<span className="count-badge">{data.length}</span>}
      >
        <div className="low-list">
          {data.length > 0 ? (
            data.map((item, index) => {
              const percent = parseFloat(item.percentage)

              return (
                <div key={index} className="low-item">
                  <div className="left">
                    <div className="avatar">
                      {item.full_name?.charAt(0).toUpperCase()}
                    </div>

                    <div>
                      <div className="name">{item.full_name}</div>
                      {/* <div className="sub-text">ID: #{item.reg_id}</div> */}
                    </div>
                  </div>

                  <div className="right">
                    <Progress
                      percent={percent}
                      size="small"
                      strokeColor={percent < 25 ? '#ff4d4f' : '#faad14'}
                      width={80}
                    />
                  </div>
                </div>
              )
            })
          ) : (
            <div className="no-data">No Students 🎉</div>
          )}
        </div>
      </Card>
    )
  }

  // ---------------- Category Data ----------------
  const categoryData = [
    {
      title: 'Orphan',
      count: stats.orphanCount,
      icon: <UserOutlined />,
      color: '#52c41a',
    },
    {
      title: 'Semi',
      count: stats.semiCount,
      icon: <TeamOutlined />,
      color: '#1890ff',
    },
    {
      title: 'POCSO',
      count: stats.pocsoCount,
      icon: <WarningOutlined />,
      color: '#faad14',
    },
    {
      title: 'Unfit Guardian',
      count: stats.unfitGuardianCount,
      icon: <AlertOutlined />,
      color: '#f5222d',
    },
    {
      title: 'Child Line',
      count: stats.childLineCount,
      icon: <TeamOutlined />,
      color: '#13c2c2',
    },
    {
      title: 'Railway Child Line',
      count: stats.railwayChildLineCount,
      icon: <TeamOutlined />,
      color: '#2f54eb',
    },
    {
      title: 'Child Help Line',
      count: stats.childHelpLineCount,
      icon: <TeamOutlined />,
      color: '#eb2f96',
    },
    {
      title: 'Missing',
      count: stats.missingCount,
      icon: <AlertOutlined />,
      color: '#722ed1',
    },
  ]

  const categoryChartData = categoryData.map((item) => ({
    name: item.title,
    value: item.count || 0,
  }))

  const COLORS = [
    '#52c41a',
    '#1890ff',
    '#faad14',
    '#f5222d',
    '#13c2c2',
    '#2f54eb',
    '#eb2f96',
    '#722ed1',
  ]

  return (
    <>
      <AdminHeader />

      <div className="dashboard-section container-fluid">
        <div className="row student-form-title p-0 m-0">
          <h3 className="section-title1 text-start mb-3">Dashboard Overview</h3>
        </div>

        <div className="row p-0 m-0">
          <div className="col-12 col-xl-2 "></div>

          <div className="col-12 col-xl-8 ">
            {/* Top Stats */}
            <Row gutter={[16, 16]} className="px-2">
              <Col xs={24} md={12}>
                <Card className="stat-card green-stat" loading={loading}>
                  <Statistic
                    title="Total Admissions"
                    value={stats.addmisionCount}
                    prefix={<UserOutlined />}
                    formatter={formatter}
                  />
                </Card>
              </Col>

              <Col xs={24} md={12}>
                <Card className="stat-card red-stat" loading={loading}>
                  <Statistic
                    title="Discharged Students"
                    value={stats.dischargeCount}
                    prefix={<LogoutOutlined />}
                    formatter={formatter}
                  />
                </Card>
              </Col>
            </Row>

            {/* Category Cards */}
            <Row
              gutter={[16, 16]}
              className="px-2"
              style={{ marginTop: '30px' }}
            >
              <Col xs={24} md={10}>
                <Card title="Category Distribution" className="shadow">
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        style={{ border: '2px solid red' }}
                        data={categoryChartData}
                        dataKey="value"
                        nameKey="name"
                        outerRadius={120}
                      >
                        {categoryChartData.map((entry, index) => (
                          <Cell
                            key={index}
                            fill={COLORS[index % COLORS.length]}
                          />
                        ))}
                      </Pie>

                      <Tooltip />

                      <Legend
                        verticalAlign="bottom"
                        align="center"
                        layout="horizontal"
                      />
                    </PieChart>

                    <Legend
                      formatter={(value, entry) => {
                        const item = categoryChartData.find(
                          (d) => d.name === value,
                        )
                        return `${value} (${item?.value})`
                      }}
                    />
                  </ResponsiveContainer>
                </Card>
              </Col>

              <Col xs={24} md={14}>
                <Card title="Form Completion Status" className="shadow">
                  <ResponsiveContainer
                    width="100%"
                    height={350}
                    className="bg-white"
                  >
                    <BarChart
                      className="p-md-2"
                      data={
                        Array.isArray(formCompletionStatus)
                          ? formCompletionStatus
                          : []
                      }
                    >
                      <XAxis
                        dataKey="name"
                        interval={0}
                        angle={-30}
                        textAnchor="end"
                        height={100}
                        style={{ fontSize: '10px' }}
                      />
                      <YAxis domain={[0, 100]} />
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Bar
                        dataKey="value"
                        fill="#e58046"
                        radius={[10, 10, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            {/* Extension Alerts */}
            <Row
              gutter={[16, 16]}
              className="px-2"
              style={{ marginTop: '40px', marginBottom: '40px' }}
            >
              <Col span={24}>
                <h4>⚠️ Extension Alerts</h4>
              </Col>

              <Col xs={24} md={8}>
                <ExtensionCard
                  className="shadow"
                  title="5 Days Remaining"
                  data={extensionData.days_5}
                  color="#f5222d"
                />
              </Col>

              <Col xs={24} md={8}>
                <ExtensionCard
                  className="shadow"
                  title="8 Days Remaining"
                  data={extensionData.days_8}
                  color="#fa8c16"
                />
              </Col>

              <Col xs={24} md={8}>
                <ExtensionCard
                  className="shadow"
                  title="15 Days Remaining"
                  data={extensionData.days_15}
                  color="#1890ff"
                />
              </Col>
            </Row>

            {/* Less than 50% form completed */}
            <Row
              gutter={[16]}
              className="px-2"
              style={{ marginBottom: '40px' }}
            >
              <Col xs={24}>
                <LowCompletionCard data={lowCompletionStudents} />
              </Col>
            </Row>

          </div>

          <div className="col-12 col-xl-2 "></div>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default Dashboard
